if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
        this.prependMedia = this.dataset.disablePrepend != 'true';
        if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();

        this.filteringOption = this.dataset.filteringOption;
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // ✅ init purchase-mode swapping (first slide + first thumb only)
        this.initPurchaseModeSwaps();
      }

      /* -----------------------------
        PURCHASE MODE (OTP vs SUB)
      ------------------------------ */

      hasPurchaseModeImages() {
        const subMain = (this.dataset.subscriptionMainImg || '').trim();
        const otpMain = (this.dataset.oneTimeMainImg || '').trim();
        const subThumb = (this.dataset.subscriptionThumbImg || '').trim();
        const otpThumb = (this.dataset.oneTimeThumbImg || '').trim();

        // If none exist, do nothing forever.
        return !!(subMain || otpMain || subThumb || otpThumb);
      }

      initPurchaseModeSwaps() {
        if (!this.hasPurchaseModeImages()) return;

        // Run once on load
        this.swapFirstMediaForMode(this.getPurchaseMode());

        // Listen for Seal subscriptions (your existing integration)
        document.addEventListener('sealsubs:selling_plan_changed', (e) => {
          const sellingPlanId = e?.detail?.selling_plan_id;
          const mode = sellingPlanId ? 'subscription' : 'one-time';
          this.swapFirstMediaForMode(mode);
        });

        // Fallback: listen to native input/select changes too
        this.bindSellingPlanInputs();
      }

      bindSellingPlanInputs() {
        // Radios
        document.querySelectorAll('input[name="selling_plan"]').forEach((el) => {
          el.addEventListener('change', () => {
            this.swapFirstMediaForMode(this.getPurchaseMode());
          });
        });

        // Select dropdown
        const select = document.querySelector('select[name="selling_plan"]');
        if (select) {
          select.addEventListener('change', () => {
            this.swapFirstMediaForMode(this.getPurchaseMode());
          });
        }
      }

      getPurchaseMode() {
        const radio = document.querySelector('input[name="selling_plan"]:checked');
        if (radio) return radio.value ? 'subscription' : 'one-time';

        const select = document.querySelector('select[name="selling_plan"]');
        if (select) return select.value ? 'subscription' : 'one-time';

        return 'subscription';
      }

      getFirstSlideImage() {
        // First slide in the viewer
        const firstSlide =
          this.elements.viewer?.querySelector('.product__media-item') ||
          this.querySelector('.product__media-item');

        if (!firstSlide) return null;

        // Match your announceLiveRegion selector first
        return (
          firstSlide.querySelector('.product__modal-opener--image img') ||
          firstSlide.querySelector('img')
        );
      }

      getFirstThumbImage() {
        if (!this.elements.thumbnails) return null;

        // First thumbnail item
        const firstThumb =
          this.elements.thumbnails.querySelector('[data-target]') ||
          this.elements.thumbnails.querySelector('.thumbnail-list__item');

        return firstThumb?.querySelector('img') || null;
      }

      swapFirstMediaForMode(mode) {
        const subMain = (this.dataset.subscriptionMainImg || '').trim();
        const otpMain = (this.dataset.oneTimeMainImg || '').trim();
        const subThumb = (this.dataset.subscriptionThumbImg || '').trim();
        const otpThumb = (this.dataset.oneTimeThumbImg || '').trim();

        const targetMain = mode === 'subscription' ? subMain : otpMain;
        const targetThumb = mode === 'subscription' ? subThumb : otpThumb;

        // ---- swap FIRST SLIDE IMAGE ----
        const img = this.getFirstSlideImage();
        if (img) {
          if (!img.dataset.wdOriginalSrc) {
            img.dataset.wdOriginalSrc = img.currentSrc || img.src || '';
            img.dataset.wdOriginalSrcset = img.getAttribute('srcset') || '';
            img.dataset.wdOriginalDataSrc = img.getAttribute('data-src') || '';
            img.dataset.wdOriginalDataSrcset = img.getAttribute('data-srcset') || '';
          }

          if (targetMain) {
            img.src = targetMain;
            img.setAttribute('srcset', targetMain);
            img.setAttribute('data-src', targetMain);
            img.setAttribute('data-srcset', targetMain);

            const picture = img.closest('picture');
            if (picture) {
              picture.querySelectorAll('source').forEach((s) => {
                s.srcset = targetMain;
                s.setAttribute('data-srcset', targetMain);
              });
            }
          } else {
            // revert to original if no URL for this mode
            img.src = img.dataset.wdOriginalSrc || img.src;
            if (img.dataset.wdOriginalSrcset) img.setAttribute('srcset', img.dataset.wdOriginalSrcset);
            if (img.dataset.wdOriginalDataSrc) img.setAttribute('data-src', img.dataset.wdOriginalDataSrc);
            if (img.dataset.wdOriginalDataSrcset) img.setAttribute('data-srcset', img.dataset.wdOriginalDataSrcset);
          }
        }

        // ---- swap FIRST THUMB IMAGE ----
        const thumbImg = this.getFirstThumbImage();
        if (thumbImg) {
          if (!thumbImg.dataset.wdOriginalThumbSrc) {
            thumbImg.dataset.wdOriginalThumbSrc = thumbImg.currentSrc || thumbImg.src || '';
            thumbImg.dataset.wdOriginalThumbSrcset = thumbImg.getAttribute('srcset') || '';
            thumbImg.dataset.wdOriginalThumbDataSrc = thumbImg.getAttribute('data-src') || '';
            thumbImg.dataset.wdOriginalThumbDataSrcset = thumbImg.getAttribute('data-srcset') || '';
          }

          if (targetThumb) {
            thumbImg.src = targetThumb;
            thumbImg.setAttribute('srcset', targetThumb);
            thumbImg.setAttribute('data-src', targetThumb);
            thumbImg.setAttribute('data-srcset', targetThumb);
          } else {
            // revert to original if no URL for this mode
            thumbImg.src = thumbImg.dataset.wdOriginalThumbSrc || thumbImg.src;
            if (thumbImg.dataset.wdOriginalThumbSrcset) thumbImg.setAttribute('srcset', thumbImg.dataset.wdOriginalThumbSrcset);
            if (thumbImg.dataset.wdOriginalThumbDataSrc) thumbImg.setAttribute('data-src', thumbImg.dataset.wdOriginalThumbDataSrc);
            if (thumbImg.dataset.wdOriginalThumbDataSrcset) thumbImg.setAttribute('data-srcset', thumbImg.dataset.wdOriginalThumbDataSrcset);
          }
        }
      }

      /* -----------------------------
        EXISTING GALLERY LOGIC
      ------------------------------ */

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
        if (filtering && currentVariant) {
          const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
          allSlides.forEach((slide) => {
            if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
              slide.classList.remove('hidden');
            } else {
              slide.classList.add('hidden');
            }
          });
        }

        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend && this.prependMedia) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (filtering && currentVariant) {
          if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
          if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;

        if (this.elements.thumbnails.vertical) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
        } else {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
